import posthog from "posthog-js";

import { analyticsContext, hashForAnalytics, isAnalyticsEnabled } from "./posthog";

export type WalletSource =
    | "header"
    | "buy_widget"
    | "swap_widget"
    | "retail_enrollment"
    | "wallet_menu"
    | "terms_modal"
    | "auto_restore"
    | "provider"
    | "unknown";
export type MigrationStep =
    | "unstake_legacy_susdv"
    | "claim_legacy_usdv"
    | "swap_legacy_usdv_to_usdc"
    | "swap_usdc_to_usdv";
export type TransactionStage = "building" | "awaiting_signature" | "signed" | "submitting" | "confirming";
export type EventSurface = "dialog" | "drawer" | "popover" | "page";
export interface WalletEventProperties {
    entry_point?: WalletSource;
    surface?: EventSurface | "main_app" | "retail_enrollment";
    connection_flow_id?: string;
    connection_attempt_id?: string;
    connection_mode?: "manual" | "restore";
    connection_method?: "wallet_standard" | "mobile_wallet_adapter" | "unknown";
    wallet_session_id?: string;
    wallet_provider?: string;
    menu_id?: string;
    view_id?: string;
    operation_id?: string;
    check_id?: string;
    migration_journey_id?: string;
    migration_attempt_id?: string;
    migration_type?: "legacy_usdv";
    retry_of_attempt_id?: string;
    attempt_origin?: "imported";
    attempt_number?: number;
    duration_ms?: number;
    ready_duration_ms?: number;
    acceptance_latency_ms?: number;
    cooldown_duration_ms?: number;
    remaining_step_count?: number;
    state?: string;
    initial_state?: string;
    last_state?: string;
    last_view?: "account" | "migration";
    view?: "loading" | "landing" | "retail";
    landing_variant?: "loading" | "projection" | "below_minimum" | "swap_from_stables" | "swap_no_stables";
    retail_view?: "disconnected" | "not_enrolled" | "enrolling" | "rewards_enabled" | "account_unavailable";
    account_status?: "pending" | "active" | "suspended" | "blacklisted" | "closed" | "none" | "unknown";
    step?: MigrationStep;
    entry_step?: MigrationStep;
    stage?: TransactionStage;
    failure_stage?: TransactionStage | "provider_connect";
    uncertain_stage?: TransactionStage;
    reason?: string;
    close_method?: string;
    trigger?: string;
    check_type?: "queue" | "eligibility" | "receipt";
    result?: "loaded" | "eligible" | "ineligible" | "confirmed" | "failed" | "pending";
    result_source?: "live" | "recovery";
    completion_source?: "live" | "recovery";
    continuity?: "new_context" | "resumed";
    retry_target?: "transaction" | "queue" | "eligibility" | "receipt" | "quote";
    decision?: "allowed" | "restricted" | "granted_public" | "granted_restricted" | "denied";
    decision_source?: "cached" | "fresh";
    link_type?: "install" | "open_wallet_browser";
    destination?: "terms" | "waitlist";
    error_code?: string;
    error_category?:
        | "user_rejected"
        | "timeout"
        | "network"
        | "unsupported"
        | "wallet_unavailable"
        | "quote"
        | "transaction"
        | "storage"
        | "validation"
        | "unknown";
    retryable?: boolean;
    failed_component?: "balances" | "account" | "both";
    observation?: "initial" | "became_claimable";
    change_source?: "provider";
    available_providers?: string[];
    installed_providers?: string[];
    checked?: boolean;
    had_checked_box?: boolean;
    wallet_connected?: boolean;
    has_usdv?: boolean;
    has_stables?: boolean;
    has_solo?: boolean;
    has_position?: boolean;
    position_disabled?: boolean;
    below_minimum?: boolean;
    has_legacy_usdv?: boolean;
    has_legacy_susdv?: boolean;
    has_legacy_dust?: boolean;
    has_claimable?: boolean;
    has_cooling?: boolean;
    has_intermediate_usdc?: boolean;
    transaction_pending?: boolean;
    attempt_pending?: boolean;
    modal_open?: boolean;
    menu_open?: boolean;
    start_observed?: boolean;
    late_result?: boolean;
    terms_document_key?: string;
    terms_version?: string;
    terms_content_digest?: string;
}

export type WalletEvent =
    | "wallet_connect:requested"
    | "wallet_connect:modal_opened"
    | "wallet_connect:step_viewed"
    | "wallet_connect:modal_closed"
    | "wallet_connect:provider_selected"
    | "wallet_connect:provider_link_clicked"
    | "wallet_connect:link_clicked"
    | "wallet_connect:location_check_started"
    | "wallet_connect:location_check_completed"
    | "wallet_connect:location_check_failed"
    | "wallet_connect:started"
    | "wallet_connect:completed"
    | "wallet_connect:failed"
    | "wallet:ready"
    | "wallet:access_check_completed"
    | "wallet:access_check_failed"
    | "wallet:account_changed"
    | "wallet:context_load_started"
    | "wallet:context_load_completed"
    | "wallet:context_load_failed"
    | "wallet:home_viewed"
    | "terms:modal_opened"
    | "terms:checkbox_toggled"
    | "terms:accepted"
    | "terms:rejected"
    | "wallet:menu_opened"
    | "wallet:menu_closed"
    | "wallet:balances_viewed"
    | "wallet:address_copy_requested"
    | "wallet:address_copied"
    | "wallet:address_copy_failed"
    | "wallet_disconnect:requested"
    | "wallet_disconnect:confirmation_viewed"
    | "wallet_disconnect:canceled"
    | "wallet_disconnect:confirmed"
    | "wallet_disconnect:started"
    | "wallet_disconnect:completed"
    | "wallet_disconnect:failed"
    | "migration:card_viewed"
    | "migration:opened"
    | "migration:step_viewed"
    | "migration:closed"
    | "migration:check_started"
    | "migration:check_completed"
    | "migration:check_failed"
    | "migration:retry_clicked"
    | "migration:cooldown_started"
    | "migration:claim_detected"
    | "migration:completed"
    | "migration:done_clicked"
    | "migration:step_requested"
    | "migration:blocked"
    | "migration:transaction_started"
    | "migration:transaction_stage_changed"
    | "migration:transaction_submitted"
    | "migration:transaction_completed"
    | "migration:transaction_failed"
    | "migration:transaction_confirmation_deferred";

export interface EventOwner {
    properties: WalletEventProperties;
    identity?: Promise<string>;
}

let fallbackId = 0;
export function eventId(): string {
    return globalThis.crypto?.randomUUID?.() ?? `event-${Date.now()}-${++fallbackId}`;
}

export function eventError(error: unknown): Pick<WalletEventProperties, "error_code" | "error_category"> {
    const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
    const value = message.toLowerCase();
    const category = /reject|denied|cancel/.test(value)
        ? "user_rejected"
        : /timeout|timed out/.test(value)
          ? "timeout"
          : /unsupported|not supported/.test(value)
            ? "unsupported"
            : /network|fetch|rpc/.test(value)
              ? "network"
              : /storage|quota/.test(value)
                ? "storage"
                : /quote/.test(value)
                  ? "quote"
                  : "unknown";
    return { error_code: category, error_category: category };
}

/** Owner snapshots keep async results on the account that started the operation. */
export function captureWalletEvent(
    event: WalletEvent,
    properties: WalletEventProperties = {},
    owner?: EventOwner
): void {
    if (!isAnalyticsEnabled() || typeof window === "undefined") return;
    if (
        /^\/(showcase|wip)(\/|$)/.test(window.location.pathname) ||
        new URLSearchParams(window.location.search).has("state")
    )
        return;
    const payload = {
        ...analyticsContext(),
        ...owner?.properties,
        ...properties,
        schema_version: 3,
        route: window.location.pathname
    };
    if (owner?.identity) {
        void owner.identity
            .then((identity) => {
                if (identity) posthog.capture(event, { ...payload, distinct_id: identity });
            })
            .catch(() => undefined);
    } else {
        try {
            posthog.capture(event, payload);
        } catch {
            /* Analytics must not block the user action. */
        }
    }
}

export function ownerForAddress(address: string, properties: WalletEventProperties = {}): EventOwner {
    return { properties, identity: hashForAnalytics(address).catch(() => "") };
}

export function migrationStep(index: number | undefined): MigrationStep | undefined {
    return index === undefined
        ? undefined
        : (["unstake_legacy_susdv", "claim_legacy_usdv", "swap_legacy_usdv_to_usdc", "swap_usdc_to_usdv"] as const)[
              index - 1
          ];
}
